import { motion } from 'framer-motion';
import { Hammer, Scissors, UtensilsCrossed, ShoppingBag, Dumbbell, Home, Sparkles, Briefcase } from 'lucide-react';

const industries = [
  {
    name: 'Contractors',
    image: '/images/contractor-confident.webp',
    icon: Hammer,
    scenario: 'Never miss a bid request',
    color: 'from-orange-500/20 to-orange-600/20',
    iconColor: 'text-orange-400'
  },
  {
    name: 'Salons & Spas',
    image: '/images/salon-owner.webp',
    icon: Scissors,
    scenario: 'Book appointments 24/7',
    color: 'from-pink-500/20 to-pink-600/20',
    iconColor: 'text-pink-400'
  },
  {
    name: 'Restaurants',
    image: '/images/restaurant-owner.webp',
    icon: UtensilsCrossed,
    scenario: 'Handle reservations instantly',
    color: 'from-red-500/20 to-red-600/20',
    iconColor: 'text-red-400'
  },
  {
    name: 'Retail Stores',
    image: '/images/retail-owner-relaxed.webp',
    icon: ShoppingBag,
    scenario: 'Capture every customer inquiry',
    color: 'from-blue-500/20 to-blue-600/20',
    iconColor: 'text-blue-400'
  },
  {
    name: 'Fitness Studios',
    image: '/images/fitness-owner.webp',
    icon: Dumbbell,
    scenario: 'Fill classes automatically',
    color: 'from-green-500/20 to-green-600/20',
    iconColor: 'text-green-400'
  },
  {
    name: 'Real Estate',
    image: '/images/realtor.webp',
    icon: Home,
    scenario: 'Qualify leads while showing',
    color: 'from-purple-500/20 to-purple-600/20',
    iconColor: 'text-purple-400'
  },
  {
    name: 'Cleaning Services',
    image: '/images/cleaning-service.webp',
    icon: Sparkles,
    scenario: 'Schedule jobs effortlessly',
    color: 'from-cyan-500/20 to-cyan-600/20',
    iconColor: 'text-cyan-400'
  },
  {
    name: 'Consultants',
    image: '/images/consultant.webp',
    icon: Briefcase,
    scenario: 'Book consultations 24/7',
    color: 'from-indigo-500/20 to-indigo-600/20',
    iconColor: 'text-indigo-400'
  }
];

export const IndustryShowcase = () => {
  return (
    <section className="py-24 bg-brand-base/50 border-y border-brand-border relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/50 to-brand-base/50 pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-brand-primary/10 border border-brand-primary/20 mb-6"
          >
            <span className="text-sm text-brand-primary font-medium">Industry Diversity</span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-5xl font-extrabold mb-6 font-display"
          >
            Built for <span className="text-brand-accent">Your</span> Industry
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-benefit text-slate-400 max-w-readable mx-auto font-semibold"
          >
            From contractors to consultants, our AI receptionists adapt to your business. See yourself in our success stories.
          </motion.p>
        </div>

        {/* Industry Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {industries.map((industry, index) => (
            <motion.div
              key={industry.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative bg-brand-dark border border-brand-border rounded-2xl overflow-hidden hover:border-brand-accent/50 transition-all duration-300"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={industry.image}
                  alt={`${industry.name} business owner using North Forge AI receptionist`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                {/* Gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-t ${industry.color} to-transparent opacity-60`} />
                
                {/* Icon badge */}
                <div className="absolute top-4 right-4 w-12 h-12 bg-brand-dark/80 backdrop-blur-sm rounded-xl flex items-center justify-center border border-brand-border group-hover:scale-110 transition-transform">
                  <industry.icon className={`w-6 h-6 ${industry.iconColor}`} />
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2 font-display group-hover:text-brand-accent transition-colors">
                  {industry.name}
                </h3>
                <p className="text-base text-slate-400">
                  {industry.scenario}
                </p>
              </div>

              {/* Hover effect border */}
              <div className="absolute inset-0 border-2 border-brand-accent/0 group-hover:border-brand-accent/20 rounded-2xl transition-all duration-300 pointer-events-none" />
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center mt-12"
        >
          <p className="text-body-lg text-slate-300 font-semibold">
            Don't see your industry? <span className="text-brand-accent">We adapt to any business.</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};
